import { ld } from "@/lib/theme/light-dark";
import {
  ClipboardList,
  FileText,
  Headphones,
  Mail,
  MessageSquare,
  Phone,
  Server,
  Share2,
  Users,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";

import { H } from "../hero-tokens";

/**
 * Nine "chaos" windows of other programs flying to the center on scroll. Skeletons,
 * only titlebars readable. `data-fly="rs,re"` is the scroll window of the flight; `scroll-stage.tsx` aims it.
 */

/** Unread badges + dots: ink, orange stays reserved for demo buttons. */
const BADGE = ld("#0B0F19", "#fafafa");

/** Geometry + identity for one fly window (the outer absolutely-positioned box). */
type FlyMeta = {
  readonly left: number;
  readonly top: number;
  readonly z: number;
  readonly width: number;
  readonly fly: string;
  readonly radius: number;
  /** Small windows use a lighter shadow than the large ones. */
  readonly small: boolean;
};

const SHADOW_LARGE =
  `0 26px 50px -24px ${ld("rgba(11,15,25,0.22)", "rgba(0,0,0,0.38)")}, 0 6px 14px -6px ${ld("rgba(11,15,25,0.10)", "rgba(0,0,0,0.2)")}`;
const SHADOW_SMALL =
  `0 12px 26px -14px ${ld("rgba(11,15,25,0.18)", "rgba(0,0,0,0.32)")}, 0 3px 8px -4px ${ld("rgba(11,15,25,0.08)", "rgba(0,0,0,0.17)")}`;

function flyBoxStyle(m: FlyMeta): CSSProperties {
  return {
    position: "absolute",
    left: m.left,
    top: m.top,
    zIndex: m.z,
    width: m.width,
    background: ld("#ffffff", "#181b1f"),
    border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
    borderRadius: m.radius,
    boxShadow: m.small ? SHADOW_SMALL : SHADOW_LARGE,
    overflow: "hidden",
    transformOrigin: "center center",
    willChange: "transform, opacity",
  };
}

const barStyle = (
  w: number | string,
  h: number,
  bg: string,
  mt?: number,
): CSSProperties => ({
  height: h,
  width: w,
  background: bg,
  borderRadius: 3,
  ...(mt !== undefined ? { marginTop: mt } : null),
});

/** The three faux traffic-light dots shown in every large window titlebar. */
function TrafficDots(): ReactNode {
  return (
    <span style={{ display: "flex", gap: 5, flex: "none" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: ld("#E1E4E9", "#373a3e"),
          }}
        />
      ))}
    </span>
  );
}

/** Large-window titlebar: traffic dots + outline icon + title + orange badge. */
function TitleBarLarge(props: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly badge: string;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "10px 13px",
        borderBottom: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
        background: ld("#F8F9FB", "#202326"),
      }}
    >
      <TrafficDots />
      <span
        style={{
          display: "inline-flex",
          color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
          flex: "none",
          marginLeft: 2,
        }}
      >
        {props.icon}
      </span>
      <span
        style={{
          fontFamily: H.sans,
          fontSize: 12.5,
          fontWeight: 600,
          color: ld("#0B0F19", "#fafafa"),
          whiteSpace: "nowrap",
        }}
      >
        {props.title}
      </span>
      <CountBadge value={props.badge} />
    </div>
  );
}

/** Small-window titlebar: icon + title + (optional) orange badge, no dots. */
function TitleBarSmall(props: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly badge?: string;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 11px",
        borderBottom: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
        background: ld("#F8F9FB", "#202326"),
      }}
    >
      <span
        style={{
          display: "inline-flex",
          color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
          flex: "none",
        }}
      >
        {props.icon}
      </span>
      <span
        style={{
          fontFamily: H.sans,
          fontSize: 11.5,
          fontWeight: 600,
          color: ld("#0B0F19", "#fafafa"),
          whiteSpace: "nowrap",
        }}
      >
        {props.title}
      </span>
      {props.badge !== undefined ? (
        <CountBadge value={props.badge} />
      ) : (
        <span style={{ marginLeft: "auto" }} />
      )}
    </div>
  );
}

function CountBadge(props: { readonly value: string }): ReactNode {
  return (
    <span
      style={{
        marginLeft: "auto",
        minWidth: 21,
        height: 18,
        padding: "0 6px",
        borderRadius: 9,
        background: BADGE,
        color: ld("#fff", "#181b1f"),
        fontFamily: H.sans,
        fontSize: 10.5,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {props.value}
    </span>
  );
}

/** Two stacked icon-rows — the shared body of the four tiny corner windows. */
function TwoRowSkeleton(props: {
  readonly rows: readonly { readonly title: string }[];
}): ReactNode {
  return (
    <div style={{ padding: "10px 11px" }}>
      {props.rows.map((row, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 9,
            alignItems: "center",
            padding: "5px 0",
          }}
        >
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: ld("#F4F5F7", "#272a2e"),
              flex: "none",
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={barStyle(row.title, 6, ld("#E1E4E9", "#373a3e"))} />
            <div style={barStyle("90%", 5, ld("#ECEEF1", "#2e3135"), 5)} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Window 0: Outlook (folder sidebar + conversation rows) ---------- */

function OutlookWindow(): ReactNode {
  const t = useTranslations("marketing.scrollStageWindows");
  const folderWidths = [60, 54, 48, 42, 36];
  return (
    <div style={flyBoxStyle(WIN0)} data-fly={WIN0.fly}>
      <TitleBarLarge
        icon={<Mail size={15} strokeWidth={1.7} />}
        title={t("outlook")}
        badge="47"
      />
      <div style={{ display: "flex", height: 286 }}>
        <div
          style={{
            width: 118,
            flex: "none",
            borderRight: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
            background: ld("#F8F9FB", "#202326"),
            padding: "11px 9px",
          }}
        >
          {folderWidths.map((w, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 6px",
                borderRadius: 7,
                marginBottom: 2,
                ...(i === 0
                  ? {
                      background: ld("#ffffff", "#181b1f"),
                      boxShadow:
                        `0 12px 26px -14px ${ld("rgba(11,15,25,0.18)", "rgba(0,0,0,0.32)")}, 0 3px 8px -4px ${ld("rgba(11,15,25,0.08)", "rgba(0,0,0,0.17)")}`,
                    }
                  : null),
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 5,
                  background: i === 0 ? "rgba(0,128,112,0.14)" : ld("#F4F5F7", "#272a2e"),
                }}
              />
              <div style={barStyle(w, 6, i === 0 ? ld("#E1E4E9", "#373a3e") : ld("#ECEEF1", "#2e3135"))} />
              {i === 0 ? (
                <span
                  style={{
                    marginLeft: "auto",
                    minWidth: 16,
                    height: 15,
                    padding: "0 4px",
                    borderRadius: 8,
                    background: BADGE,
                    color: ld("#fff", "#181b1f"),
                    fontSize: 9,
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: H.sans,
                  }}
                >
                  47
                </span>
              ) : null}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 11,
                padding: "10px 13px",
                borderBottom: `1px solid ${ld("#F4F5F7", "#272a2e")}`,
                ...(i < 3 ? { background: ld("rgba(11,15,25,0.025)", "rgba(250,250,250,0.03)") } : null),
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: ld("#F4F5F7", "#272a2e"),
                  flex: "none",
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div style={barStyle("50%", 7, ld("#E1E4E9", "#373a3e"))} />
                  <div style={barStyle(28, 6, ld("#ECEEF1", "#2e3135"))} />
                </div>
                <div style={barStyle("82%", 6, ld("#ECEEF1", "#2e3135"), 7)} />
              </div>
              {i < 3 ? (
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: BADGE,
                    flex: "none",
                    marginTop: 5,
                  }}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Window 1: Dokumente (folder tree + file-list rows) ---------- */

function FolderGlyph(props: { readonly color: string }): ReactNode {
  return (
    <span style={{ display: "inline-flex", color: props.color, flex: "none" }}>
      <FileText size={13} strokeWidth={1.7} style={{ display: "block" }} />
    </span>
  );
}

function DokumenteWindow(): ReactNode {
  const t = useTranslations("marketing.scrollStageWindows");
  const fileRows = ["70%", "52%", "82%", "46%", "64%"];
  const treeRows: readonly {
    readonly w: number;
    readonly indent: boolean;
    readonly active: boolean;
  }[] = [
    { w: 52, indent: false, active: true },
    { w: 38, indent: true, active: false },
    { w: 46, indent: true, active: false },
    { w: 56, indent: false, active: false },
    { w: 42, indent: false, active: false },
    { w: 48, indent: false, active: false },
  ];
  return (
    <div style={flyBoxStyle(WIN1)} data-fly={WIN1.fly}>
      <TitleBarLarge
        icon={<FileText size={15} strokeWidth={1.7} />}
        title={t("dokumente")}
        badge="3"
      />
      <div style={{ display: "flex", height: 286 }}>
        <div
          style={{
            width: 128,
            flex: "none",
            borderRight: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
            background: ld("#F8F9FB", "#202326"),
            padding: "11px 9px",
          }}
        >
          {treeRows.map((row, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: row.indent ? "6px 7px 6px 17px" : "6px 7px",
                borderRadius: 7,
                marginBottom: 1,
                ...(row.active
                  ? {
                      background: ld("#ffffff", "#181b1f"),
                      boxShadow:
                        `0 12px 26px -14px ${ld("rgba(11,15,25,0.18)", "rgba(0,0,0,0.32)")}, 0 3px 8px -4px ${ld("rgba(11,15,25,0.08)", "rgba(0,0,0,0.17)")}`,
                    }
                  : null),
              }}
            >
              <FolderGlyph
                color={row.active ? ld("#008070", "#5cbfad") : ld("rgba(11,15,25,0.3)", "rgba(250,250,250,0.3)")}
              />
              <div
                style={barStyle(row.w, 6, row.active ? ld("#E1E4E9", "#373a3e") : ld("#ECEEF1", "#2e3135"))}
              />
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "9px 13px",
              borderBottom: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
            }}
          >
            <div style={barStyle(44, 6, ld("#ECEEF1", "#2e3135"))} />
            <span
              style={{ display: "inline-flex", color: ld("rgba(11,15,25,0.3)", "rgba(250,250,250,0.3)") }}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: "block" }}
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </span>
            <div style={barStyle(58, 6, ld("#E1E4E9", "#373a3e"))} />
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 5,
                  background: ld("#F4F5F7", "#272a2e"),
                }}
              />
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 5,
                  background: ld("#F4F5F7", "#272a2e"),
                }}
              />
            </div>
          </div>
          {fileRows.map((w, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "48px 1fr 64px",
                gap: 10,
                padding: "9px 14px",
                borderBottom: `1px solid ${ld("#F4F5F7", "#272a2e")}`,
                alignItems: "center",
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 26,
                  borderRadius: 4,
                  background: ld("#F4F5F7", "#272a2e"),
                  border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: ld("rgba(11,15,25,0.3)", "rgba(250,250,250,0.3)"),
                }}
              >
                <FileText
                  size={12}
                  strokeWidth={1.7}
                  style={{ display: "block" }}
                />
              </span>
              <div style={barStyle(w, 7, ld("#E1E4E9", "#373a3e"))} />
              <div style={barStyle("70%", 6, ld("#ECEEF1", "#2e3135"))} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Window 2: Telefonanlage (call-log rows) ---------- */

function TelefonanlageWindow(): ReactNode {
  const t = useTranslations("marketing.scrollStageWindows");
  const rows: readonly {
    readonly iconBg: string;
    readonly iconFg: string;
    readonly trailBg: string;
  }[] = [
    { iconBg: ld("#F1F3F5", "#2a2d31"), iconFg: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"), trailBg: BADGE },
    { iconBg: ld("#F1F3F5", "#2a2d31"), iconFg: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"), trailBg: BADGE },
    { iconBg: "rgba(0,128,112,0.08)", iconFg: ld("#006557", "#6cbcac"), trailBg: ld("#ECEEF1", "#2e3135") },
    { iconBg: ld("#F4F5F7", "#272a2e"), iconFg: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"), trailBg: ld("#ECEEF1", "#2e3135") },
  ];
  return (
    <div style={flyBoxStyle(WIN2)} data-fly={WIN2.fly}>
      <TitleBarLarge
        icon={<Phone size={15} strokeWidth={1.7} />}
        title={t("telefonanlage")}
        badge="3"
      />
      <div style={{ padding: "2px 0" }}>
        {rows.map((row, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "10px 13px",
              borderBottom: `1px solid ${ld("#F4F5F7", "#272a2e")}`,
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: row.iconBg,
                color: row.iconFg,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "none",
              }}
            >
              <Phone size={12} strokeWidth={1.7} style={{ display: "block" }} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={barStyle("54%", 7, ld("#E1E4E9", "#373a3e"))} />
              <div style={barStyle("40%", 5, ld("#ECEEF1", "#2e3135"), 6)} />
            </div>
            <div style={barStyle(26, 6, row.trailBg)} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Window 3: Vorgänge & Portal (status-pill rows) ---------- */

function VorgaengeWindow(): ReactNode {
  const t = useTranslations("marketing.scrollStageWindows");
  const pillColors = ["#008070", ld("#C9CED6", "#4a4e54"), BADGE, "#008070"];
  return (
    <div style={flyBoxStyle(WIN3)} data-fly={WIN3.fly}>
      <TitleBarLarge
        icon={<Share2 size={15} strokeWidth={1.7} />}
        title={t("vorgaengePortal")}
        badge="8"
      />
      <div style={{ padding: "2px 0" }}>
        {pillColors.map((color, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "11px 14px",
              borderBottom: `1px solid ${ld("#F4F5F7", "#272a2e")}`,
            }}
          >
            <div style={barStyle(30, 7, ld("#ECEEF1", "#2e3135"))} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={barStyle("56%", 7, ld("#E1E4E9", "#373a3e"))} />
              <div style={barStyle("40%", 5, ld("#ECEEF1", "#2e3135"), 6)} />
            </div>
            <span
              style={{
                width: 40,
                height: 15,
                borderRadius: 999,
                background: color,
                flex: "none",
                opacity: 0.9,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Window 4: Microsoft Teams (channel sidebar + chat bubbles) ---------- */

function TeamsWindow(): ReactNode {
  const t = useTranslations("marketing.scrollStageWindows");
  const channels = [54, 49, 44, 39];
  const bubbles: readonly { readonly l1: string; readonly l2: string }[] = [
    { l1: "76%", l2: "60%" },
    { l1: "62%", l2: "46%" },
    { l1: "70%", l2: "54%" },
  ];
  return (
    <div style={flyBoxStyle(WIN4)} data-fly={WIN4.fly}>
      <TitleBarLarge
        icon={<MessageSquare size={15} strokeWidth={1.7} />}
        title={t("microsoftTeams")}
        badge="23"
      />
      <div style={{ display: "flex", height: 262 }}>
        <div
          style={{
            width: 112,
            flex: "none",
            borderRight: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
            background: ld("#F8F9FB", "#202326"),
            padding: "10px 8px",
          }}
        >
          {channels.map((w, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: "6px 7px",
                borderRadius: 7,
                marginBottom: 1,
                ...(i === 0 ? { background: ld("#ffffff", "#181b1f") } : null),
              }}
            >
              <span
                style={{
                  fontFamily: H.label,
                  fontSize: 12,
                  color: ld("rgba(11,15,25,0.3)", "rgba(250,250,250,0.3)"),
                }}
              >
                #
              </span>
              <div style={barStyle(w, 6, i === 0 ? ld("#E1E4E9", "#373a3e") : ld("#ECEEF1", "#2e3135"))} />
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0, padding: "12px 13px" }}>
          {bubbles.map((b, i) => (
            <div key={i} style={{ display: "flex", gap: 9, marginBottom: 12 }}>
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: ld("#F4F5F7", "#272a2e"),
                  flex: "none",
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={barStyle("28%", 6, ld("#E1E4E9", "#373a3e"))} />
                <div
                  style={{
                    background: ld("#F8F9FB", "#202326"),
                    borderRadius: "0 9px 9px 9px",
                    padding: "8px 10px",
                    marginTop: 5,
                  }}
                >
                  <div style={barStyle(b.l1, 6, ld("#ECEEF1", "#2e3135"))} />
                  <div style={barStyle(b.l2, 5, ld("#F4F5F7", "#272a2e"), 5)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Window 5: Server / Ablage (single "Letztes Backup" row) ---------- */

function ServerWindow(): ReactNode {
  const t = useTranslations("marketing.scrollStageWindows");
  return (
    <div style={flyBoxStyle(WIN5)} data-fly={WIN5.fly}>
      <TitleBarSmall
        icon={<Server size={13} strokeWidth={1.7} />}
        title={t("serverAblage")}
      />
      <div style={{ padding: "10px 11px" }}>
        <div
          style={{
            fontSize: 11.5,
            color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
            marginBottom: 7,
            fontFamily: H.sans,
          }}
        >
          {t("letztesBackup")}
        </div>
        <div
          style={{
            display: "flex",
            gap: 9,
            alignItems: "center",
            padding: "5px 0",
          }}
        >
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: ld("#F4F5F7", "#272a2e"),
              flex: "none",
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={barStyle("64%", 6, ld("#E1E4E9", "#373a3e"))} />
            <div style={barStyle("90%", 5, ld("#ECEEF1", "#2e3135"), 5)} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- The nine windows, rendered together ---------- */

const WIN0: FlyMeta = {
  left: 0,
  top: 24,
  z: 4,
  width: 430,
  fly: "0.300,0.660",
  radius: 14,
  small: false,
};
const WIN1: FlyMeta = {
  left: 624,
  top: 20,
  z: 3,
  width: 440,
  fly: "0.330,0.682",
  radius: 14,
  small: false,
};
const WIN2: FlyMeta = {
  left: 250,
  top: 128,
  z: 5,
  width: 300,
  fly: "0.390,0.726",
  radius: 14,
  small: false,
};
const WIN3: FlyMeta = {
  left: 118,
  top: 336,
  z: 7,
  width: 430,
  fly: "0.420,0.748",
  radius: 14,
  small: false,
};
const WIN4: FlyMeta = {
  left: 556,
  top: 372,
  z: 8,
  width: 404,
  fly: "0.450,0.770",
  radius: 14,
  small: false,
};
const WIN5: FlyMeta = {
  left: 872,
  top: 328,
  z: 6,
  width: 188,
  fly: "0.480,0.792",
  radius: 12,
  small: true,
};
const WIN6: FlyMeta = {
  left: 864,
  top: 470,
  z: 9,
  width: 196,
  fly: "0.510,0.814",
  radius: 12,
  small: true,
};
const WIN7: FlyMeta = {
  left: 18,
  top: 494,
  z: 7,
  width: 198,
  fly: "0.540,0.836",
  radius: 12,
  small: true,
};
const WIN8: FlyMeta = {
  left: 430,
  top: 566,
  z: 10,
  width: 198,
  fly: "0.570,0.858",
  radius: 12,
  small: true,
};

export function FlyWindows(): ReactNode {
  const t = useTranslations("marketing.scrollStageWindows");
  return (
    <>
      <OutlookWindow />
      <DokumenteWindow />
      <TelefonanlageWindow />
      <VorgaengeWindow />
      <TeamsWindow />
      <ServerWindow />
      <div style={flyBoxStyle(WIN6)} data-fly={WIN6.fly}>
        <TitleBarSmall
          icon={<Users size={13} strokeWidth={1.7} />}
          title={t("eigentuemerVers")}
          badge="1"
        />
        <TwoRowSkeleton rows={[{ title: "70%" }, { title: "54%" }]} />
      </div>
      <div style={flyBoxStyle(WIN7)} data-fly={WIN7.fly}>
        <TitleBarSmall
          icon={<Headphones size={13} strokeWidth={1.7} />}
          title={t("kiAnrufbeantworter")}
          badge="4"
        />
        <TwoRowSkeleton rows={[{ title: "70%" }, { title: "54%" }]} />
      </div>
      <div style={flyBoxStyle(WIN8)} data-fly={WIN8.fly}>
        <TitleBarSmall
          icon={<ClipboardList size={13} strokeWidth={1.7} />}
          title={t("uebergabeformulare")}
          badge="6"
        />
        <TwoRowSkeleton rows={[{ title: "70%" }, { title: "54%" }]} />
      </div>
    </>
  );
}
